import {Avatar, Menu, Modal, Popover} from "antd";
import {CalendarOutlined, MailOutlined, PhoneOutlined} from "@ant-design/icons";
import React from "react";
import {IEmployeeDetail} from "../../../domains/models/employee";
import {IFileState} from "../types/employee-details.types";
import dayjs from "dayjs";
import {removeUnderScoreWithLowerCase} from "../../../utility/utility";
import Locale from "../../../core/locale/locale";
import CopyContent from "../../../common/copy-content";
import '../../../styles/component/employee-details/employee-details-header.scss';
import EmployeeAvatar from "../../employee/avatar/employee-avatar";


interface employeeDetailsHeaderProps {
    viewState: { isModalVisible: boolean; popoverVisible: boolean };
    isActiveEmployee: boolean;
    navigateToDocument: () => void;
    getInitials: (e: IEmployeeDetail | null) => string;
    employeeData: IEmployeeDetail | null;
    navigateToEmployeeEdit: () => void;
    user: any;
    setViewState: React.Dispatch<React.SetStateAction<{ isModalVisible: boolean; popoverVisible: boolean }>>;
    PopoverMenuItems: any[];
    profileState: IFileState;
}

const EmployeeDetailHeader = (props: employeeDetailsHeaderProps) => {
    const skillsLength = (props.employeeData?.experiences?.primarySkills?.length || 0) + (props.employeeData?.experiences?.secondarySkills?.length || 0);

    const employeementType = props.employeeData?.experiences?.employmentType ? removeUnderScoreWithLowerCase(props.employeeData?.experiences?.employmentType) : ""

    const joiningDate = props.employeeData?.joiningDate ? dayjs(props.employeeData?.joiningDate).format(Locale.displayDateWithMonth) : "";

    const calculateTotalExperience = () => {
        const previousExps = props.employeeData?.experiences?.previousExperiences || [];
        let totalMonths = 0;

        // Add previous experience (years + months)
        previousExps.forEach(exp => {
            const years = exp.years || 0;
            const months = exp.months || 0;
            totalMonths += (years * 12) + months;
        });

        // Add current company experience (from joiningDate)
        const joiningDate = props.employeeData?.joiningDate;
        if (joiningDate) {
            const start = dayjs(joiningDate);
            const todayDate = dayjs();
            // Calculating total months from joining date to today
            const currentMonths = todayDate.diff(start, "month");
            // Add current company months to previous company months
            totalMonths += currentMonths;
        }

        return {
            totalMonths,
            // Convert total months back to years (with 1 decimal)
            totalYears: (totalMonths / 12).toFixed(1)
        };
    };

    const {totalMonths, totalYears} = calculateTotalExperience();

    return (
        <div className="employee-detail__header">
            {/* Profile Photo Section */}
            <div className="employee-detail__header-image">
                <Popover
                    trigger="click"
                    placement="bottom"
                    open={props.viewState.popoverVisible}
                    onOpenChange={() => props.setViewState((prev: any) => ({
                        ...prev,
                        popoverVisible: !props.viewState.popoverVisible
                    }))}
                    content={
                        <Menu items={props.PopoverMenuItems} className={'employee-detail__profile-menu'}/>
                    }
                >
                    <div className="employee-profile-img" style={{cursor: 'pointer'}}>
                        <EmployeeAvatar
                            name={props.employeeData?.name}
                            size={100}
                            profileURL={props.profileState.profileUrl.baseLocation ?? undefined}
                        />
                        {/*<div className="avatar-overlay"/>*/}
                    </div>
                </Popover>

                {/* View Image Modal */}
                <Modal
                    className={'hrms-app profile-view-modal'}
                    open={props.viewState.isModalVisible}
                    footer={null}
                    onCancel={() => props.setViewState((prev: any) => ({...prev, isModalVisible: false}))}
                >
                    <div className="profile-view">
                        <img
                            src={props.profileState.profileUrl.baseLocation!}
                            alt="Profile"
                        />
                    </div>
                </Modal>
            </div>

            {/* Employee Info */}
            <div className="employee-detail__header-info">
                <h1 className="name">
                    {`${props.employeeData?.name?.firstName ?? ""} ${props.employeeData?.name?.middleName ?? ""} ${props.employeeData?.name?.lastName ?? ""}`}
                    {props.employeeData?.active ? (
                            <span className="status-badge">ACTIVE</span>
                        )
                        :
                        (<span className="status-badge">INACTIVE</span>)
                    }
                </h1>

                <h4 className="designation">
                    {props.employeeData?.designation?.displayName ? props.employeeData?.designation?.displayName : ""}
                </h4>
                <div className={'emp-code-and-type'}>
                    <p>{props.employeeData?.employeeCode ? props.employeeData?.employeeCode : ""}</p>
                    <p>{(employeementType) ?? " "}</p>
                </div>

                {
                    props.employeeData?.reportingManagerName && (
                        <div className={"employee-detail-reporting-manager"}>
                            <p>Reporting to: </p>
                            {props.employeeData?.reportingManagerName?.firstName ?? ""} {props.employeeData?.reportingManagerName?.lastName ?? ""}
                        </div>
                    )
                }

                <div className={'mail-number-date'}>
                    {props.employeeData?.officialEmail && (
                        <h4 className="email">
                            <>
                                <MailOutlined/>
                                {' '}
                                <CopyContent content={props.employeeData?.officialEmail}>
                                    <a
                                        href={`https://mail.google.com/mail/?view=cm&fs=1&to=${props.employeeData?.officialEmail}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="email-link"
                                    >
                                        {props.employeeData.officialEmail}
                                    </a>
                                </CopyContent>
                            </>
                        </h4>
                    )}

                    {props.employeeData?.mobileNumber?.number && (
                        <h4 className="email">
                            <>
                                <PhoneOutlined style={{transform: 'rotate(97deg)'}}/>{" "}
                                {/*{props.employeeData.mobileNumber.countryCode}{" "}*/}
                                {/*<a href={`tel:${props.employeeData.mobileNumber.countryCode}${props.employeeData.mobileNumber.number}`} className={'email-link'}>{props.employeeData.mobileNumber.number}</a>{' '}*/}
                                {/*<CopyOutlined onClick={()=>window.navigator.clipboard.writeText(props.employeeData?.mobileNumber?.number ?? '')}/>*/}
                                <CopyContent
                                    content={props.employeeData.mobileNumber.countryCode + '' + props.employeeData.mobileNumber.number}>
                                    <a href={`tel:${props.employeeData.mobileNumber.countryCode}${props.employeeData.mobileNumber.number}`}
                                       className={'email-link'}>{props.employeeData.mobileNumber.countryCode} {props.employeeData.mobileNumber.number}</a>{' '}
                                </CopyContent>
                            </>
                        </h4>
                    )}

                    {props.employeeData?.joiningDate && (
                        <h4 className="email">
                            <>
                                <CalendarOutlined/> Joined: {joiningDate}
                            </>
                        </h4>
                    )}
                </div>
            </div>

            <div className="employee-detail__header-stats">

                {totalMonths > 0 && (
                    <div className="stat-card">
                        <div className="stat-value">
                            {totalYears}+
                        </div>
                        <div className="stat-label">EXPERIENCE</div>
                    </div>
                )}

                {
                    skillsLength > 0 && (
                        <div className="stat-card">
                            <div className="stat-value">
                                {skillsLength}
                            </div>
                            <div className="stat-label"> SKILLS</div>
                        </div>
                    )
                }
            </div>
        </div>
    )
}

export default EmployeeDetailHeader;
